import React, { Suspense, lazy } from "react";
import {
  Navigate,
  Route,
  Routes
} from "react-router-dom";
import { Box, CircularProgress, Skeleton, Stack } from "@mui/material";

import App from "./App";
import { ErrorBoundary } from "./components/system/ErrorBoundary";

// Lazy load components for better performance
const Home = lazy(() => import("./components/Home"));
const BusinessAds = lazy(() => import("./components/BusinessAds").then(m => ({ default: m.BusinessAds })));
const Tickets = lazy(() => import("./components/Tickets"));
const Videos = lazy(() => import("./components/video/Videos"));
const Sponsors = lazy(() => import("./components/Sponsors").then(m => ({ default: m.Sponsors })));
const Login = lazy(() => import("./components/LoginScreen/Login").then(m => ({ default: m.Login })));
const BusinessPage = lazy(() => import("./pages/BusinessPage").then(m => ({ default: m.BusinessPage })));
const StoresPage = lazy(() => import("./pages/StoresPage").then(m => ({ default: m.StoresPage })));
const AffiliatesPage = lazy(() => import("./pages/AffiliatesPage").then(m => ({ default: m.AffiliatesPage })));
const AffiliateBusinessPage = lazy(() => import("./pages/AffiliateBusinessPage").then(m => ({ default: m.AffiliateBusinessPage })));
const ServiceItem = lazy(() => import("./components/ServiceItem").then(m => ({ default: m.ServiceItem })));
const DebugPanel = lazy(() => import("./pages/DebugPanel"));
const RaffleDrawingPage = lazy(() => import("./pages/RaffleDrawingPage"))
const PublicUserQRPage = lazy(() => import("./pages/PublicUserQRPage"))
const Profile = lazy(() => import("./components/Profile"))

const env = process.env;
// Only allow debug routes on non-production builds when explicitly enabled
const isDebugEnabled = env.NODE_ENV !== "production" || env.REACT_APP_DEBUG === "true";
// import { SponsorsArray } from "../components/Sponsors"
// Reusable loading fallback component
const PageLoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      p: 3,
    }}
  >
    <Stack spacing={3} alignItems="center" sx={{ maxWidth: 600, width: '100%' }}>
      <CircularProgress size={48} />
      <Skeleton variant="text" width="60%" height={40} />
      <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 2 }} />
    </Stack>
  </Box>
);

export const renderRoutePage = (componentName: string, children: React.ReactNode) => (
  <ErrorBoundary componentName={componentName}>
    <Suspense fallback={<PageLoadingFallback />}>{children}</Suspense>
  </ErrorBoundary>
);

export const AppRoutes = () => {
  return (
     <Routes>
      <Route path="" element={<App />}>
        <Route path="videos" element={renderRoutePage("VideosPage", <Videos />)} />
        <Route path="*" element={<Navigate to="/" replace />} />
        <Route path="" element={renderRoutePage("HomePage", <Home />)} />
        <Route path="login" element={renderRoutePage("LoginPage", <Login />)} />
        <Route path="profile" element={renderRoutePage("ProfilePage", <Profile />)} />
        <Route path="tickets" element={renderRoutePage("TicketsPage", <Tickets />)} />
        <Route path="sponsors" element={renderRoutePage("SponsorsPage", <Sponsors />)} />
        <Route path="sponsors/:id" element={renderRoutePage("SponsorDetailPage", <Sponsors />)} />
        <Route path="businesses" element={renderRoutePage("BusinessPage", <BusinessPage />)} />
        <Route path="affiliates" element={renderRoutePage("AffiliatesPage", <AffiliatesPage />)} />
        <Route path="affiliates/:id" element={renderRoutePage("AffiliateBusinessPage", <AffiliateBusinessPage />)} />
        <Route path="businesses/:id" element={renderRoutePage("BusinessDetailPage", <BusinessPage />)} />
        <Route path="stores/:id" element={renderRoutePage("StoresPage", <StoresPage />)} />
        <Route path="businesses/:id/services/:sid" element={renderRoutePage("ServiceItemPage", <ServiceItem />)} />
        {isDebugEnabled ? (
          <Route path="debug" element={renderRoutePage("DebugPanelPage", <DebugPanel />)} />
        ) : null}
        <Route path="raffles/:id/drawing" element={renderRoutePage("RaffleDrawingPage", <RaffleDrawingPage />)} />
      </Route>
      <Route path="ads/:i" element={renderRoutePage("BusinessAdsPage", <BusinessAds />)} />
      <Route path="uqr" element={renderRoutePage("PublicUserQRPage", <PublicUserQRPage />)} />
    </Routes>
  );
};
